import { ChangeDetectionStrategy, Component, EventEmitter, Input, OnInit, Output, inject } from "@angular/core";
import { UntilDestroy, untilDestroyed } from "@ngneat/until-destroy";
import { AccountSelectionService, AsinService, UserSelectionService } from "@front/m19-services";
import { Observable, startWith, switchMap } from "rxjs";
import { FormControl, ReactiveFormsModule } from "@angular/forms";
import { SovBrandDateType } from "../../share-of-voice/share-of-voice.component";
import { Catalog } from "@front/m19-models";
import { AccountMarketplace } from "@front/m19-api-client";
import { TranslocoDirective } from "@jsverse/transloco";
import { IIconComponent, ITooltipDirective, SpinnerComponent } from "@front/m19-ui";
import { PALETTE } from "@front/m19-metrics";

@UntilDestroy()
@Component({
  selector: "app-sov-main-table",
  templateUrl: "./sov-main-table.component.html",
  styleUrls: ["./sov-main-table.component.scss"],
  imports: [TranslocoDirective, ReactiveFormsModule, SpinnerComponent, IIconComponent, ITooltipDirective],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class SovMainTableComponent implements OnInit {
  private userSelectionService = inject(UserSelectionService);
  private accountSelectionService = inject(AccountSelectionService);
  private asinService = inject(AsinService);

  @Input() set sovByBrand(data: SovBrandDateType | undefined) {
    if (!data) return;
    const reducedMap = new Map<string, number>();
    data.forEach((innerMap, key) => {
      reducedMap.set(key, this.sumAllDateSovInRange(innerMap));
    });
    this._sovByBrandDate = new Map([...reducedMap.entries()].sort((a, b) => b[1] - a[1]));

    this.setBrands(this._sovByBrandDate);

    this.brandSelection = Array.from(this._sovByBrandDate.keys()).slice(0, 5);
    this.emitBrandSelection.emit(this.brandSelection);
  }

  @Input() brandColors?: Map<string, string>;
  @Input() loading?: boolean;

  @Output() emitHoveredBrand = new EventEmitter<string>();
  @Output() emitBrandSelection = new EventEmitter<string[]>();

  _sovByBrandDate?: Map<string, number>;

  brandSelection?: string[];
  hoveredBrand?: string;

  filter!: FormControl;
  filter$!: Observable<string>;

  allBrands?: string[];
  filteredBrands?: string[];
  catalogBrands?: Set<string>;
  userBrands?: string[];

  private inventory$: Observable<Catalog> = this.accountSelectionService.singleAccountMarketplaceSelection$.pipe(
    switchMap((am: AccountMarketplace) => {
      return this.asinService.getCatalog(am.accountId, am.marketplace);
    }),
  );

  ngOnInit() {
    this.filter = new FormControl("");
    this.filter$ = this.filter.valueChanges.pipe(startWith(""));

    this.inventory$.pipe(untilDestroyed(this)).subscribe((c: Catalog) => {
      this.catalogBrands = c.brands();
    });

    this.filter$.pipe(untilDestroyed(this)).subscribe((f) => {
      if (!this._sovByBrandDate) return;
      this.updateBrandFilter(f);
    });
  }

  private setBrands(sovByBrandDate: Map<string, number>) {
    this.updateBrandFilter(this.filter?.value);
    this.allBrands = [...sovByBrandDate].sort((a: any, b: any) => b[1][1] - a[1][0]).map(([b, _s]) => b);
  }

  private updateBrandFilter(filter: string) {
    this.userBrands = this.catalogBrands
      ? [...this.catalogBrands].filter((b: string) => this._sovByBrandDate?.has(b) && b.toLowerCase().includes(filter))
      : [];
    this.filteredBrands = [...this._sovByBrandDate!]
      .map(([b, _s]) => b)
      .filter((b: string) => !this.userBrands?.includes(b) && b.toLowerCase().includes(filter?.toLowerCase()));
  }

  // Sum all the sov for date in date range
  private sumAllDateSovInRange(map: Map<number, number>): number {
    const dateRange = this.userSelectionService.getDateRange();

    // Filter map to keep date in range
    const inRangeMap = new Map<number, number>(
      [...map.entries()].filter(([key, _value]) => {
        // Get rid of GMT stuff
        const dateWithoutTime = new Date(key);
        const startWithoutTime = dateRange.start.startOf("day").toJSDate();
        const endWithoutTime = dateRange.end.startOf("day").toJSDate();

        return dateWithoutTime >= startWithoutTime && dateWithoutTime <= endWithoutTime;
      }),
    );

    const sovArray: number[] = Array.from(inRangeMap.values());
    if (sovArray.length == 0) return 0;

    const mean = sovArray.reduce((acc, curr) => acc + curr, 0) / sovArray.length;

    return Math.ceil(mean);
  }

  toggleBrandSelection(brand: string, selectOnly = false) {
    if (selectOnly) {
      this.brandSelection = [brand];
    } else {
      const brandIndex = this.brandSelection?.indexOf(brand);

      if (brandIndex && brandIndex > -1) {
        this.brandSelection?.splice(brandIndex, 1);
      } else {
        this.brandSelection?.push(brand);
      }
    }
    this.emitBrandSelection.emit(this.brandSelection);
  }

  brandMouseEnter(brand: string) {
    if (this.isBrandSelected(brand) > -1) {
      this.hoveredBrand = brand;
      this.emitHoveredBrand.emit(brand);
    } else this.brandMouseLeave(); // When hovering a non-selected brand, reset colors
  }

  brandMouseLeave() {
    this.hoveredBrand = undefined;
    this.emitHoveredBrand.emit(undefined);
  }

  isBrandSelected(brand: string): number {
    return this.brandSelection?.indexOf(brand) ?? -1;
  }

  protected readonly PALETTE = PALETTE;
}
